import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useContext } from 'react';
import logo from '../images/logo.png';
import { formatMemberId, formatName } from '../utils/dataTransformers';
import {
  FaCalendarAlt,
  FaShoppingCart,
  FaClipboardList,
  FaUserTie,
  FaUserCircle,
  FaSignOutAlt,
  FaChevronDown,
  FaCog,
  FaIdBadge,
  FaToggleOn,
  FaToggleOff,
  FaStore,
  FaBoxOpen,
  FaChartLine,
  FaDollarSign,
  FaStoreAlt
} from 'react-icons/fa';
import NotificationCenter from './NotificationCenter';
import Avatar from './Avatar';
import UserContext from './UserContext';
import Logout from './Logout';
import HttpInterceptor from '../lib/HttpInterceptor';


function StudentHeader() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const menuRef = useRef(null);
  const profileTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isPWA, setIsPWA] = useState(false);

  const { session: student, setSession } = useContext(UserContext);
  const [isReseller, setIsReseller] = useState(false);
  const [resellerDropdownOpen, setResellerDropdownOpen] = useState(false);

  // Check screen size and set mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if user is a reseller
  useEffect(() => {
    const checkResellerStatus = async () => {
      if (!student?.id) return;
      try {
        const response = await HttpInterceptor.get('/api/resellers/profile');
        if (response.data?.success && response.data?.data) {
          setIsReseller(response.data.data.status === 'approved');
        }
      } catch (error) {
        // User is not a reseller or not approved
        setIsReseller(false);
      }
    };
    checkResellerStatus();
  }, [student?.id]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Close dropdowns when clicking outside or when cursor leaves
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(profileTimeoutRef.current);
    };
  }, []);

  const handleProfileInteraction = () => {
    if (isMobile) {
      setIsProfileOpen(!isProfileOpen);
      setIsMenuOpen(false);
    } else {
      clearTimeout(profileTimeoutRef.current);
      setIsProfileOpen(true);
    }
  };

  const handleProfileLeave = () => {
    if (!isMobile) {
      profileTimeoutRef.current = setTimeout(() => {
        setIsProfileOpen(false);
      }, 200);
    }
  };

  const handleDropdownEnter = () => {
    if (!isMobile) {
      clearTimeout(profileTimeoutRef.current);
    }
  };

  const handleDropdownLeave = () => {
    if (!isMobile) {
      setIsProfileOpen(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsProfileOpen(false);
  };

  // Default student data if not provided
  // const student = studentData || {
  //   id: 'STU001',
  //   name: "Member Name",
  //   email: "student@yogvital.com",
  //   profileImage: null,
  //   joinDate: new Date().toISOString().split('T')[0],
  //   status: "active"
  // };

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-primary to-light text-dark shadow-lg animate-fade-in">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-2">
        {/* Logo and Brand */}
        <div className="flex items-center">
          <Link to={student?.role === 'customer' ? '/student/orders' : '/student/home'} className="relative group/logo">
            <div className="absolute -inset-1 bg-gradient-to-br from-white/30 to-transparent blur-[10px] rounded-full opacity-70 group-hover/logo:opacity-100 transition-opacity duration-300"></div>
            <div className="relative transform group-hover/logo:scale-[1.02] transition-transform duration-300">
              <img
                src={logo}
                alt="Yogvital Logo"
                className="h-15 w-auto drop-shadow-[0_2px_4px_rgba(255,255,255,0.3)]"
              />
            </div>
          </Link>

          <Link to={student?.role === 'customer' ? '/student/orders' : '/student/home'} className="ml-3 group/text">
            <h1 className="text-3xl font-bold mt-1 font-display group-hover/text:text-accent transition-colors duration-300">
              Yogvital
            </h1>
          </Link>
        </div>

        {/* Desktop Menu */}
        <div className={`${isMobile ? 'hidden' : 'flex'} items-center space-x-2`}>
          <Link
            to="/student/market"
            className="flex items-center px-4 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200 group"
          >
            <FaStore className="mr-2 text-dark group-hover:text-accent transition-colors" />
            <span>Marketplace</span>
          </Link>

          {student?.role !== 'customer' && (
            <>
              {/* Reseller Menu Dropdown */}
              {isReseller && (
                <div
                  className="relative"
                  onMouseEnter={() => !isMobile && setResellerDropdownOpen(true)}
                  onMouseLeave={() => !isMobile && setResellerDropdownOpen(false)}
                >
                  <button
                    onClick={() => isMobile && setResellerDropdownOpen(!resellerDropdownOpen)}
                    className="flex items-center px-4 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200 group"
                  >
                    <FaStoreAlt className="mr-2 text-dark group-hover:text-accent transition-colors" />
                    <span>Reseller</span>
                    <FaChevronDown className={`ml-1 text-xs transition-transform ${resellerDropdownOpen ? 'transform rotate-180' : ''}`} />
                  </button>
                  {resellerDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <Link
                        to="/reseller/dashboard"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setResellerDropdownOpen(false)}
                      >
                        <FaChartLine className="mr-3 text-gray-500" />
                        <span>Dashboard</span>
                      </Link>
                      <Link
                        to="/reseller/products"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setResellerDropdownOpen(false)}
                      >
                        <FaStore className="mr-3 text-gray-500" />
                        <span>My Products</span>
                      </Link>
                      <Link
                        to="/reseller/orders"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setResellerDropdownOpen(false)}
                      >
                        <FaBoxOpen className="mr-3 text-gray-500" />
                        <span>My Orders</span>
                      </Link>
                      <Link
                        to="/reseller/earnings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setResellerDropdownOpen(false)}
                      >
                        <FaDollarSign className="mr-3 text-gray-500" />
                        <span>Earnings</span>
                      </Link>
                    </div>
                  )}
                </div>
              )}

              <Link
                to="/student/services"
                className="flex items-center px-4 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200 group"
              >
                <FaShoppingCart className="mr-2 text-dark group-hover:text-accent transition-colors" />
                <span>Services</span>
              </Link>
              <Link
                to="/student/appointments"
                className="flex items-center px-4 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200 group"
              >
                <FaCalendarAlt className="mr-2 text-dark group-hover:text-accent transition-colors" />
                <span>My Appointments</span>
              </Link>
              <Link
                to="/student/plans"
                className="flex items-center px-4 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200 group"
              >
                <FaClipboardList className="mr-2 text-dark group-hover:text-accent transition-colors" />
                <span>My Plans</span>
              </Link>
              <Link
                to="/student/trainer"
                className="flex items-center px-4 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200 group"
              >
                <FaUserTie className="mr-2 text-dark group-hover:text-accent transition-colors" />
                <span>My Mentors</span>
              </Link>
            </>
          )}

          <Link
            to="/student/orders"
            className="flex items-center px-4 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200 group"
          >
            <FaBoxOpen className="mr-2 text-dark group-hover:text-accent transition-colors" />
            <span>My Orders</span>
          </Link>
        </div>

        {/* Header Right Tools (Notifications & Profile) */}
        {!isMobile && (
          <div className="flex items-center space-x-2">
            <NotificationCenter />
            
            <div
              className="relative ml-2"
              ref={profileRef}
              onMouseEnter={() => setIsProfileOpen(true)}
              onMouseLeave={handleProfileLeave}
            >
              <button
                className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-accent/10 transition-colors duration-200"
                aria-label="Profile menu"
              >
                <Avatar src={student?.profileImage} name={student?.name || 'User'} size={32} className="border-2 border-accent/50" />
                <FaChevronDown className={`text-dark/80 transition-transform ${isProfileOpen ? 'transform rotate-180' : ''}`} />
              </button>

              {isProfileOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-xs text-gray-500">ID: {formatMemberId(student?.id, student?.name) || 'N/A'}</p>
                    <p className="text-sm font-medium text-gray-900">{formatName(student?.name) || 'User'}</p>
                  </div>
                  {student?.role !== 'customer' && (
                    <Link
                      to={student?.isTrainer && student?.trainerStatus === 'active' ? '/trainer/trainer-profile' : '/student/profile'}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <FaUserCircle className="mr-3 text-gray-500" />
                      <span>My Profile</span>
                    </Link>
                  )}
                  <Link
                    to="/student/orders"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <FaBoxOpen className="mr-3 text-gray-500" />
                    <span>My Orders</span>
                  </Link>
                  <button
                    onClick={() => {
                      Logout(navigate, setSession);
                      setIsProfileOpen(false);
                    }}
                    className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                  >
                    <FaSignOutAlt className="mr-3 text-gray-500" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Header Right Section */}
        {isMobile && (
          <div className="flex items-center space-x-4">
            <NotificationCenter />
            <div className="relative" ref={profileRef}>
              <button onClick={handleProfileInteraction} className="focus:outline-none">
                <Avatar src={student?.profileImage} name={student?.name || 'User'} size={40} className="border-2 border-accent/50" />
              </button>
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{formatName(student?.name) || 'User'}</p>
                  </div>
                  <Link to="/student/orders" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => setIsProfileOpen(false)}>
                    <FaBoxOpen className="mr-3 text-gray-500" />
                    <span>My Orders</span>
                  </Link>
                  <button onClick={() => { Logout(navigate, setSession); setIsProfileOpen(false); }} className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100">
                    <FaSignOutAlt className="mr-3 text-gray-500" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
            {!isPWA && (
              <button onClick={toggleMenu} className="text-dark p-2">
                <div className="w-6 h-6 flex flex-col justify-between items-center">
                  {isMenuOpen ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  ) : (
                    <>
                      <span className="block h-0.5 w-full bg-dark"></span>
                      <span className="block h-0.5 w-full bg-dark my-1.5"></span>
                      <span className="block h-0.5 w-full bg-dark"></span>
                    </>
                  )}
                </div>
              </button>
            )}
          </div>
        )}
      </nav>

      {/* Mobile Menu with Slide Animation - Hidden in PWA */}
      {isMobile && !isPWA && (
        <div
          id="mobile-menu"
          className={`overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96' : 'max-h-0'}`}
          ref={menuRef}
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="bg-gradient-to-b from-primary/95 to-light/95 px-4 py-2">
            <Link
              to="/student/market"
              className="flex items-center px-4 py-3 rounded-md hover:bg-accent/10 transition-colors duration-200 focus:outline-none focus:bg-accent/10"
              onClick={() => setIsMenuOpen(false)}
            >
              <FaStore className="mr-3 text-dark" aria-hidden="true" />
              <span>Marketplace</span>
            </Link>
            <Link
              to="/student/orders"
              className="flex items-center px-4 py-3 rounded-md hover:bg-accent/10 transition-colors duration-200 focus:outline-none focus:bg-accent/10"
              onClick={() => setIsMenuOpen(false)}
            >
              <FaBoxOpen className="mr-3 text-dark" aria-hidden="true" />
              <span>My Orders</span>
            </Link>
            {student?.role !== 'customer' && (
              <>
                <Link
                  to="/student/services"
                  className="flex items-center px-4 py-3 rounded-md hover:bg-accent/10 transition-colors duration-200 focus:outline-none focus:bg-accent/10"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FaShoppingCart className="mr-3 text-dark" aria-hidden="true" />
                  <span>Services</span>
                </Link>
                <Link
                  to="/student/appointments"
                  className="flex items-center px-4 py-3 rounded-md hover:bg-accent/10 transition-colors duration-200 focus:outline-none focus:bg-accent/10"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FaCalendarAlt className="mr-3 text-dark" aria-hidden="true" />
                  <span>My Appointments</span>
                </Link>
                <Link
                  to="/student/plans"
                  className="flex items-center px-4 py-3 rounded-md hover:bg-accent/10 transition-colors duration-200 focus:outline-none focus:bg-accent/10"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FaClipboardList className="mr-3 text-dark" aria-hidden="true" />
                  <span>My Plans</span>
                </Link>
                <Link
                  to="/student/trainer"
                  className="flex items-center px-4 py-3 rounded-md hover:bg-accent/10 transition-colors duration-200 focus:outline-none focus:bg-accent/10"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FaUserTie className="mr-3 text-dark" aria-hidden="true" />
                  <span>My Mentors</span>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default StudentHeader;